import {
	GITLAB_ALLOWED_PATHS,
	GitLabAllowedPath,
} from 'constants/gitlabNavigation';
import history from 'lib/history';
import { getIframeAllowedOrigins } from 'utils/envConfig';

const GITLAB_TO_O11Y_ROUTES: Record<string, GitLabAllowedPath> = {
	'logs/logs-explorer': '/logs/logs-explorer',
	'messaging-queues': '/messaging-queues/overview',
	'api-monitoring/explorer': '/api-monitoring/explorer',
	'metrics-explorer/summary': '/metrics-explorer/summary',
	'infrastructure-monitoring/hosts': '/infrastructure-monitoring/hosts',
};

class GitLabNavigationHandler {
	private isInIframe: boolean;

	private allowedOrigins: string[];

	private boundHandler = this.handleMessage.bind(this);

	constructor() {
		this.isInIframe = window.self !== window.top;

		this.allowedOrigins = getIframeAllowedOrigins();
	}

	init(): void {
		if (!this.isInIframe) {
			return;
		}

		window.addEventListener('message', this.boundHandler);

		this.sendReadyMessage();
	}

	private isAllowedOrigin(origin: string): boolean {
		return this.allowedOrigins.some((allowed) => {
			if (
				process.env.NODE_ENV !== 'production' &&
				allowed === 'http://localhost' &&
				origin.startsWith('http://localhost:')
			) {
				return true;
			}
			return origin === allowed;
		});
	}

	private handleMessage(event: MessageEvent): void {
		if (!this.isAllowedOrigin(event.origin)) {
			return;
		}

		if (!event.data || typeof event.data !== 'object') {
			return;
		}

		const { type, path, source } = event.data;

		if (type !== 'NAVIGATE_TO' || source !== 'gitlab-sidebar') {
			return;
		}

		GitLabNavigationHandler.navigateToPath(path);
	}

	private static navigateToPath(gitlabPath: string): void {
		const o11yRoute = GITLAB_TO_O11Y_ROUTES[gitlabPath];

		if (!o11yRoute) {
			const fallbackRoute = `/${gitlabPath}`;

			if (!GitLabNavigationHandler.isValidO11yPath(fallbackRoute)) {
				console.warn(`Blocked navigation to invalid path: ${fallbackRoute}`);
				history.push('/services');
				return;
			}

			history.push(fallbackRoute);
			return;
		}

		if (!GitLabNavigationHandler.isValidO11yPath(o11yRoute)) {
			console.warn(`Blocked navigation to invalid mapped path: ${o11yRoute}`);
			history.push('/services');
			return;
		}

		history.push(o11yRoute);
	}

	public static isValidO11yPath(path: string): boolean {
		return (GITLAB_ALLOWED_PATHS as readonly string[]).includes(path);
	}

	sendReadyMessage(): void {
		if (!window.parent) {
			return;
		}

		const message = {
			type: 'O11Y_READY',
			timestamp: Date.now(),
			source: 'signoz',
		};

		this.allowedOrigins.forEach((origin) => {
			window.parent.postMessage(message, origin);
		});
	}

	destroy(): void {
		window.removeEventListener('message', this.boundHandler);
	}
}

const gitlabNavHandler = new GitLabNavigationHandler();

export default gitlabNavHandler;
export { GITLAB_TO_O11Y_ROUTES, GitLabNavigationHandler };
